<template>
  <div class="profile-page">
    <div class="profile-page__inner _cnt">
      <div class="profile-page__top">
        <div class="profile-page__box">
          <div class="profile-page__title _h1">{{ pageTitle }}</div>
        </div>
        <div class="profile-page__line _line"><span></span></div>
      </div>
      <div class="profile-page__body">
        <div class="profile-page__tabs">
          <BaseButton
            v-for="tab in tabs"
            :key="tab.key"
            :class="[
              'profile-page__tab _md',
              { active: activeTab === tab.key },
            ]"
            @click="changeTab(tab.key)"
            :theme="ButtonThemes.TAB"
          >
            {{ tab.label }}
          </BaseButton>
          <BaseButton class="profile-page__tab _md" @click="logout">
            {{ $t('Log Out') }}
          </BaseButton>
        </div>
        <component
          :is="currentTabComponent"
          :user="user"
          :orderHistory="orderHistory"
          :balance="balance"
          :countryOptions="countryOptions"
          :minTopupAmount="minTopupAmount"
          :paymentMethods="paymentMethods"
          @updatePersonalInfo="updatePersonalInfo"
          @updateBillingInfo="updateBillingInfo"
          @updateProfile="updateProfile"
          @updatePassword="updatePassword"
          @openWithdraw="openWithdraw"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton, { ButtonThemes } from '@/components/base/BaseButton.vue'
import ChangePassword from './tabs/ChangePassword.vue'
import MyBalance from './tabs/MyBalance.vue'
import OrderHistory from './tabs/OrderHistory.vue'
import PersonalInformation from './tabs/PersonalInformation.vue'

export default {
  name: 'ProfilePage',

  components: {
    MyBalance,
    OrderHistory,
    ChangePassword,
    PersonalInformation,
    BaseButton,
  },

  props: {
    isAuth: {
      type: Boolean,
      required: true,
    },
    orderHistory: {
      type: Array,
      required: true,
    },
    minTopupAmount: {
      type: Number,
      required: true,
    },
    paymentMethods: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      ButtonThemes,
      tabs: [
        {
          key: 'personal-information',
          label: this.$t('Personal information'),
        },
        { key: 'my-balance', label: this.$t('Payment history') },
        { key: 'order-history', label: this.$t('Order history') },
      ],
      activeTab: 'my-balance',
      user: {
        email: '',
        name: '',
        surname: '',
        phone: '',
        phoneCountryCode: null,
        address: '',
        country: '',
        city: '',
        zip: '',
        balance: 0,
        steamId: '',
        steamProfileUrl: null,
        avatarMedium: '',
        status: '',
      },
      countryOptions: [],
    }
  },
  computed: {
    fullName() {
      return `${this?.user?.name} ${this?.user?.surname}`.trim()
    },
    currentTabComponent() {
      switch (this.activeTab) {
        case 'my-balance':
          return 'MyBalance'
        case 'order-history':
          return 'OrderHistory'
        case 'change-password':
          return 'ChangePassword'
        case 'personal-information':
          return 'PersonalInformation'
        default:
          return 'MyBalance'
      }
    },
    pageTitle() {
      const map = {
        'personal-information': this.$t('My profile'),
        'my-balance': this.$t('Payment history'),
        'order-history': this.$t('Order history'),
      }
      return map[this.activeTab] || this.$t('My profile')
    },
  },
  mounted() {
    if (!this.isAuth) {
      this.$router.push('/signin')
    }
  },
  watch: {
    '$route.params.page'(newPage) {
      if (newPage && this.tabs.some((tab) => tab.key === newPage)) {
        this.activeTab = newPage
      } else {
        this.activeTab = 'my-balance'
      }
    },
    isAuth(newVal) {
      if (!newVal) {
        this.$router.push('/signin')
      }
    },
  },
  methods: {
    openWithdraw() {
      this.$emit('openWithdraw')
    },
    changeTab(tabKey) {
      this.activeTab = tabKey
      this.$router.push({ name: 'ProfilePage', params: { page: tabKey } })
    },

    logout() {
      this.$emit('logout')
    },

    async fetchUserData() {
      try {
        const response = await this.$http.get('user/profile')
        if (response.status === 200 && response.data) {
          this.user.name = response.data.name
          this.user.surname = response.data.surname
          this.user.email = response.data.email
          this.user.phone = response.data.phone
          this.user.country = response.data.country
          this.user.city = response.data.city
          this.user.zip = response.data.zip
          this.user.address = response.data.address
          this.user.balance = response.data.balance
          this.user.steamId = response.data.steamId
          this.user.steamProfileUrl = response.data.steam_profile_url
          this.user.avatarMedium = response.data.avatar_medium
          this.user.status = response.data.status
        }
      } catch (error) {
        console.log('errro')

        //  this.$router.push({ path: "/" });
        console.error('Error fetching user data:', error)
      }
    },

    async updatePersonalInfo(data) {
      try {
        await this.$http.put('user/profile', data)
        this.personalInfo = data
      } catch (error) {
        console.error('Error updating personal information:', error)
      }
    },

    async updateBillingInfo(data) {
      try {
        await this.$http.put('user/profile', data)
        this.billingInfo = data
      } catch (error) {
        console.error('Error updating billing information:', error)
      }
    },

    async updateProfile(data) {
      try {
        await this.$http.put('user/profile', data)
        this.personalInfo = {
          firstName: data.name,
          lastName: data.surname,
          email: data.email,
          phone: data.phone,
        }
        this.billingInfo = {
          country: data.country,
          city: data.city,
          zipCode: data.zip,
          address: data.address,
        }
      } catch (error) {
        console.error('Error updating profile:', error)
      }
    },

    async updatePassword(passwordData) {
      try {
        const data = {
          oldPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        }
        await this.$http.post('user/change-password', data)

        this.$message({
          message: 'Password updated successfully!',
          type: 'success',
        })
      } catch (error) {
        console.error('Error updating password:', error)

        this.$message({
          message: 'Error updating password. Please try again.',
          type: 'error',
        })
      }
    },

    async fetchCountryOptions() {
      try {
        const response = await this.$http.get('countries')
        this.countryOptions = response.data.payload
      } catch (error) {
        console.error('Error fetching country options:', error)
      }
    },
  },

  created() {
    const page = this.$route.params.page
    if (page && this.tabs.some((tab) => tab.key === page)) {
      this.activeTab = page
    }

    this.fetchUserData()
    this.fetchCountryOptions()
  },
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.profile-page {
  @include adaptiveValue('padding-top', 50, 25);
  @include adaptiveValue('padding-bottom', 50, 20);
  @include header-indent;
  &__inner {
  }
  &__top {
    &:not(:last-child) {
      @include adaptiveValue('margin-bottom', 37, 20);
    }
  }
  &__box {
    display: flex;
    gap: 20px;
    &:not(:last-child) {
      @include adaptiveValue('margin-bottom', 15, 10);
    }
  }
  &__back {
    align-self: center;
    min-width: 40px;
    height: 40px;
    background-color: var(--hint-color);
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease 0s;
    svg {
      path {
        stroke-width: 2px;
        stroke: var(--primary-color);
      }
    }
    @media (any-hover: hover) {
      &:hover {
        background-color: var(--hint-hover-color);
      }
    }
    @media (max-width: $md5) {
      &:not(:last-child) {
        margin-bottom: 15px;
      }
    }
  }
  &__title {
    margin: 0 auto;
  }
  &__line {
    max-width: 710px;
    margin-left: auto;
    margin-right: auto;
  }
  &__body {
  }
  &__tabs {
    display: flex;
    &:not(:last-child) {
      @include adaptiveValue('margin-bottom', 40, 15);
    }
    @media (max-width: $md2) {
      flex-wrap: wrap;
    }
  }
  &__tab {
    flex: 0 1 25%;
    cursor: pointer;
    background-color: transparent;
    border-style: solid;
    border-color: transparent;
    @include adaptiveValue('border-bottom-width', 15, 2);
    transition: all 0.3s ease 0s;
    color: var(--primary-color);
    pointer-events: auto;
    @include adaptiveValue('padding', 15, 10, 1470, 768, 1);
    @media (any-hover: hover) {
      &:hover {
        color: var(--hint-color);
      }
    }
    @media (max-width: $md2) {
      flex: 0 1 50%;
    }
    @media (max-width: $md3) {
      padding: 6px 10px;
    }
    @media (max-width: $md5) {
      flex: 1 1 100%;
    }
    &.active {
      border-color: var(--primary-color);
      color: var(--hint-color);
      @media (any-hover: hover) {
        &:hover {
          pointer-events: none;
        }
      }
    }
  }
}
</style>
